import { useEffect, useState } from 'react';
import { fetchProtectedObjectUrl } from '../api/client';

/**
 * 需要令牌才能读取的敏感上传目录。
 * 必须与后端 `main.ts` 的 `sensitiveUploadPrefixes` 保持一致：
 * 后端用 `req.path.startsWith(...)` 判定，这里用 `includes` 是因为库里存的可能是绝对地址
 * （uploads.controller 按请求 host 拼 `${protocol}://${host}/uploads/...`）。
 */
const PROTECTED_PREFIXES = ['/uploads/id-cards/', '/uploads/payment-vouchers/'];

export function isProtectedUpload(url: string): boolean {
  return PROTECTED_PREFIXES.some((prefix) => url.includes(prefix));
}

/**
 * 把上传文件地址换成可直接渲染的地址。
 *
 * 非敏感地址（头像、通用图片）原样返回，浏览器自行加载；
 * 敏感地址带 `Authorization` 头取回 Blob 后返回 object URL，令牌不进 URL，
 * 并在地址变化或组件卸载时自动 revoke，避免 Blob 内存泄漏。
 *
 * 加载中与失败均返回空串——调用方的 `<img>` 会显示 alt / 破图占位，
 * 这比渲染一个 401 的错误页快照更合适。
 */
export function useProtectedUploadUrl(url: string | null | undefined): string {
  const protectedUrl = url && isProtectedUpload(url) ? url : null;
  const [objectUrl, setObjectUrl] = useState('');

  useEffect(() => {
    if (!protectedUrl) return;

    let settled = false;
    let created: string | null = null;

    fetchProtectedObjectUrl(protectedUrl)
      .then((blobUrl) => {
        // 地址已变或组件已卸载：直接释放，不要写回 state
        if (settled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        created = blobUrl;
        setObjectUrl(blobUrl);
      })
      .catch(() => {
        if (!settled) setObjectUrl('');
      });

    return () => {
      settled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [protectedUrl]);

  if (!url) return '';
  return protectedUrl ? objectUrl : url;
}
